/**
 * Razorpay Payment Component
 * Handles subscription payments using Razorpay
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, CreditCard, Shield } from 'lucide-react';
import { SubscriptionPlan, formatPrice } from '@/constants/pricing';
import { supabase } from '@/services/supabase/supabase';


// Razorpay types
declare global {
  interface Window {
    Razorpay: any;
  }
}

interface RazorpayPaymentProps {
  plan: SubscriptionPlan;
  billingCycle: 'monthly' | 'yearly';
  onSuccess?: (paymentId: string, subscriptionIdOrOrderId: string) => void;
  onError?: (error: any) => void;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
  size?: 'sm' | 'default' | 'lg';
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
}

export function RazorpayPayment({
  plan,
  billingCycle,
  onSuccess,
  onError,
  disabled = false,
  className = '',
  children,
  size = 'default',
  variant = 'default'
}: RazorpayPaymentProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const amount = billingCycle === 'monthly' ? plan.price.monthly : plan.price.yearly;
  const displayAmount = formatPrice(amount, plan.price.currency);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    const isLoaded = await loadRazorpayScript();
    if (!isLoaded) {
      toast({
        title: "Payment Error",
        "data-title": true,
        description: "Razorpay SDK failed to load. Please check your connection.",
        "data-description": true,
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      // Get user session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Please log in to continue');
      }

      const planIdKey = `${plan.id}-${billingCycle}`; // e.g., "core-monthly" or "plus-yearly"

      // Call API to create subscription
      const response = await fetch('https://node-async-consumer.chandru-vijayan101999.workers.dev/api/billing/createSubscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ planId: planIdKey })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to initiate subscription');
      }

      const data = await response.json();
      // Expecting { subscriptionId: string, key: string } per user request
      const { subscriptionId, key } = data;

      if (!subscriptionId || !key) {
        throw new Error('Invalid response from subscription API');
      }

      const options = {
        key: key,
        subscription_id: subscriptionId,
        name: "Plot Studio",
        description: `${plan.name} (${billingCycle})`,
        handler: function (response: any) {
          console.log("Payment Successful:", response);
          if (onSuccess) {
            onSuccess(response.razorpay_payment_id, response.razorpay_subscription_id);
          }
          toast({
            title: "Subscription Active",
            description: "Your subscription has been successfully activated.",
            variant: "default"
          });
        },
        theme: {
          color: "#3399cc"
        },
        prefill: {
          name: session.user.user_metadata?.full_name,
          email: session.user.email
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response: any) {
        console.error("Payment Failed:", response.error);
        if (onError) onError(response.error);
        toast({
          title: "Payment Failed",
          "data-title": true,
          description: response.error.description || "Payment could not be completed.",
          "data-description": true,
          variant: "destructive"
        });
      });

      rzp.open();

    } catch (error: any) {
      console.error('Payment Error:', error);
      if (onError) onError(error);
      toast({
        title: "Payment Error",
        "data-title": true,
        description: error.message || "Something went wrong initializing payment.",
        "data-description": true,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      className={className}
      onClick={handlePayment}
      disabled={disabled || isLoading}
    >
      {isLoading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Processing...
        </>
      ) : (
        children || (
          <>
            <CreditCard className="mr-2 h-4 w-4" />
            {amount === 0 ? 'Free Plan' : `Pay ${displayAmount}`}
          </>
        )
      )}
    </Button>
  );
}

// Security badge component
export function PaymentSecurityBadge() {
  return (
    <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground mt-2">
      <Shield className="h-3 w-3" />
      <span>Secured by Razorpay • 256-bit SSL encryption</span>
    </div>
  );
}
